import React from 'react';
import { GenericModal } from 'src/components/Modals/GenericModal';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Divider, FormGroup } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { FormControlLabel, Radio } from '@mui/material';
import { useStacklineTheme } from '@stackline/ui';
import { useVariationsFilterStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/stores/VariationsFilterStore';
import { VariationsDetailGridMetricType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';

interface VariationsFilterModalProps {
  dropdownOption: VariationsDetailGridMetricType;
}

const VariationsFilterModal: React.FC<VariationsFilterModalProps> = ({ dropdownOption }) => {
  const { isOpen, closeModal, resetFilters, applyFilters, getFilterOptions } = useVariationsFilterStore();
  const theme = useStacklineTheme();

  const filterOptions = getFilterOptions(dropdownOption);

  const handleApply = () => {
    applyFilters();
  };

  const handleClear = () => {
    resetFilters();
  };

  return (
    <GenericModal
      open={isOpen}
      onClose={closeModal}
      sx={{
        '& .MuiPaper-root': {
          padding: '18px 24px 16px 24px',
          width: '450px'
        },
        '& .genericModal__body': {
          height: '372px'
        },
        '& .genericModal__header': {
          position: 'relative',
          paddingBottom: '18px',
          justifyContent: 'center',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '-24px',
            width: 'calc(100% + 48px)',
            height: '1px',
            backgroundColor: colors.graysSecondary
          },
          '& h4': {
            width: '100%',
            textAlign: 'center'
          },
          svg: {
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-100%)'
          }
        }
      }}
    >
      <GenericModal.Header title="Variations Filter" onClose={closeModal} />
      <GenericModal.Body>
        <Flex flexDirection="column" gap={24} sx={{ padding: '24px 0 40px' }}>
          <Text variant="body2">Use the filters below to view specific variations.</Text>

          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            {filterOptions.map((option) => (
              <FormControlLabel
                key={option.id}
                checked={option.isChecked}
                onChange={(_, checked) => option.setTempFilter(checked)}
                label={option.label}
                value={option.id}
                control={
                  <Radio
                    disableRipple
                    disableTouchRipple
                    sx={{
                      '&, &.Mui-checked': {
                        color: theme.colors.primary
                      },
                      '& .MuiSvgIcon-root': {
                        width: '16px',
                        height: '16px'
                      }
                    }}
                  />
                }
                sx={{
                  margin: '0',
                  alignItems: 'center',
                  height: '19px',
                  gap: '8px',
                  flexShrink: 0,
                  width: 'fit-content',
                  '& .MuiSwitch-switchBase': {
                    padding: '0'
                  },
                  '& span': {
                    padding: '0'
                  },
                  '& .MuiTypography-root': {
                    letterSpacing: 0.28,
                    maxWidth: 'unset',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: theme.colors.primary
                  }
                }}
              />
            ))}
          </FormGroup>
        </Flex>
      </GenericModal.Body>
      <Divider
        sx={{
          margin: '0 -24px',
          width: 'calc(100% + 48px)'
        }}
      />
      <Flex
        gap={16}
        justifyContent="space-between"
        sx={{
          paddingTop: '16px'
        }}
      >
        <SlButton onClick={handleClear}>Clear</SlButton>
        <SlButton onClick={handleApply} variant="contained">
          Apply
        </SlButton>
      </Flex>
    </GenericModal>
  );
};

export default VariationsFilterModal;
